import { Star } from "lucide-react";
import { ScrollReveal } from "@/components/ScrollReveal";
import { GOOGLE_REVIEWS } from "@/data/constants";

const ReviewCard = ({ review, size }) => {
  // size: "sm" | "md" | "lg" — controls width; height flexes with content
  const widthClass =
    size === "lg" ? "w-[420px] sm:w-[460px]" :
    size === "sm" ? "w-[300px] sm:w-[320px]" :
                    "w-[360px] sm:w-[390px]";

  return (
    <article
      className={`${widthClass} shrink-0 bg-white border border-[#EDEDED] rounded-2xl p-6 shadow-[0_4px_20px_-8px_rgba(0,0,0,0.06)] hover:shadow-[0_12px_36px_-14px_rgba(232,89,10,0.28)] hover:-translate-y-1 transition-all duration-300`}
      data-testid={`review-card-${review.id}`}
    >
      {/* Header */}
      <header className="flex items-start gap-3 mb-3">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-white text-base font-semibold flex-shrink-0 select-none"
          style={{ background: review.avatarColor }}
          aria-hidden="true"
        >
          {review.initial}
        </div>
        <div className="min-w-0">
          <span className="block text-[15px] font-semibold text-[#333] truncate">{review.name}</span>
          <p className="text-[11px] text-[#8C8C8C] truncate mt-0.5">{review.meta}</p>
        </div>
      </header>

      {/* Stars + time */}
      <div className="flex items-center gap-2 mb-3">
        <div className="flex gap-0.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star
              key={i}
              className={`w-3.5 h-3.5 ${i < review.rating ? "text-[#E8590A] fill-[#E8590A]" : "text-gray-200"}`}
              strokeWidth={0}
            />
          ))}
        </div>
        <span className="text-[11px] text-[#8C8C8C]">{review.timeAgo}</span>
      </div>

      {/* Text */}
      <p className={`text-[13.5px] leading-relaxed text-[#333] ${size === "sm" ? "line-clamp-6" : "line-clamp-8"}`}>
        {review.text}
      </p>
    </article>
  );
};

/* Deterministic size pattern to visually mix cards (some big, some small). */
const SIZES = ["md", "lg", "sm", "md", "lg", "sm"];

export const ReviewsMarquee = () => {
  // Duplicate items so the CSS translateX(-50%) loop reads seamlessly
  const doubled = [...GOOGLE_REVIEWS, ...GOOGLE_REVIEWS];

  return (
    <section
      id="reviews-section"
      className="py-16 md:py-24 bg-[#F5F5F5] overflow-hidden"
      data-testid="reviews-section"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 mb-10 lg:mb-14">
        <ScrollReveal>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-[52px] font-bold text-[#333] leading-[1.05] tracking-tight">
            Наші <span className="text-[#E8590A]">відгуки</span>
          </h2>
        </ScrollReveal>
      </div>

      {/* Single marquee row */}
      <div className="reviews-marquee-viewport relative overflow-hidden">
        {/* Edge fade masks (over the grey section bg) */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 sm:w-28 bg-gradient-to-r from-[#F5F5F5] to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 sm:w-28 bg-gradient-to-l from-[#F5F5F5] to-transparent z-10" />
        <div className="reviews-marquee-track left">
          {doubled.map((r, i) => (
            <ReviewCard key={`${r.id}-${i}`} review={r} size={SIZES[i % SIZES.length]} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ReviewsMarquee;
